import {
  ChartEmptyState,
  ChartEmptyStateType,
} from '@/components/dashboards/admin';
import { LineChart } from '@/ui/charts/line-chart';
import { FlexContainer } from '@/ui/styled-ui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CardInfo } from './CardInfo';
import { SCLegend } from './SCLegend';

const StyledCardContainer = styled(FlexContainer)<{ isDemo?: boolean }>`
  background-color: ${({ isDemo }) =>
    isDemo ? 'var(--purple-background)' : 'var(--beige-main)'};
  padding: var(--default-padding) var(--big-padding);
  border-radius: var(--medium-border-radius);
  margin: 0;
  overflow: hidden;

  &.stretch-children {
    .children {
      overflow-y: hidden;
      overflow-x: auto;
      padding-bottom: var(--medium-padding);

      > div {
        flex: 1;

        > div {
          width: 100%;
        }
      }
    }
  }

  .demo-overlay {
    position: relative;
    ${({ isDemo }) =>
      isDemo &&
      `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: var(--purple-background);
      opacity: 0.5;
      z-index: 1;
    }
  `}
  }
`;

type SCOverTimeProps = {
  overTimeParsed: {
    values: number[];
    labels: string[];
  };
  isDemo?: boolean;
};

export const SCOverTime: React.FC<SCOverTimeProps> = ({
  overTimeParsed,
  isDemo,
}) => {
  const { t } = useTranslation();
  const hasNoData =
    !overTimeParsed?.values?.length ||
    overTimeParsed.values.every((v) => v === 0);

  return (
    <StyledCardContainer
      id="sc-over-time-card"
      direction="column"
      justify="space-between"
      gap={24}
      isDemo={isDemo}
    >
      <CardInfo
        id="security-culture-over-time"
        title={t('security.culture.overTime')}
        tooltip={t('security.culture.overTime.tooltip')}
        redTitle={isDemo ? t('security.culture.notMeasured') : undefined}
        isDemo={isDemo}
      />
      {!isDemo && hasNoData ? (
        <ChartEmptyState
          type={ChartEmptyStateType.SC}
          noBorder
          noMargin
          transparent
          title={t('security.culture.overTime.empty.title')}
          description={t('security.culture.overTime.empty.description')}
        />
      ) : (
        <>
          <LineChart
            id="sc-over-time"
            values={overTimeParsed.values}
            labels={overTimeParsed.labels}
            isPurple
          />
          <SCLegend isDemo={isDemo} hideOtherOrganisations />
        </>
      )}
    </StyledCardContainer>
  );
};
